import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AnimatedLoader,
    ErrorAlert,
    HorizontalFlex,
    Input,
} from 'smartsuite-ui';
import {
    isAlphaSpaceHyphen,
    isAlphanumeric,
    isArrivalDateValid,
    isReservationNumberLongEnough,
} from '../utils/validationUtil';
import { getReservationStatusUri } from '../constants/apiConstants.js';

export default function EnrollmentCard() {
    const location = useLocation();
    const [reservationId, setReservationId] = useState('');
    const [reservationLastName, setReservationLastName] = useState('');
    const [arrivalDate, setArrivalDate] = useState('');
    const [errorStatus, setErrorStatus] = useState(
        // If the later enrollment steps fail they will redirect back to this page with an error status in the location state.
        location.state?.errorStatus ?? null,
    );
    const [isSubmit, setIsSubmit] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const enrollmentRequestUrl = `/waltdisneyworld/enrollmentRequest?${new URLSearchParams(
        {
            reservationId: reservationId.trim(),
            lastName: reservationLastName.trim(),
            arrivalDate,
        },
    ).toString()}`;

    const reservationStatusQuery = useQuery({
        queryKey: ['reservationStatus', reservationId, reservationLastName],
        queryFn: async () => {
            const response = await fetch(
                getReservationStatusUri(
                    reservationId,
                    reservationLastName.trim(),
                    arrivalDate,
                ),
            );
            if (!response.ok) {
                setErrorStatus(response.status);

                setIsSubmit(false);
                return response;
            }
            setErrorStatus(null);
            const parsedResponse = await response.json();

            const basePath = `./${parsedResponse.reservationId}/${parsedResponse.reservationPrimaryLastName}`;
            const queryParams = parsedResponse.dateOfArrival
                ? `?arrivalDate=${parsedResponse.dateOfArrival}`
                : '';
            if (parsedResponse.status === 'Enrolled') {
                navigate(`${basePath}${queryParams}`);
                return parsedResponse;
            }

            navigate(`${basePath}/reservationFlightDetails${queryParams}`);
            return parsedResponse;
        },
        enabled: isEnrollmentInputsValid() && isSubmit,
        retry: false,
    });

    function isReservationNumberValid() {
        return (
            isAlphanumeric(reservationId) &&
            isReservationNumberLongEnough(reservationId)
        );
    }

    function isLastNameValid() {
        return isAlphaSpaceHyphen(reservationLastName);
    }

    function isArrivalDateValidLocal() {
        return isArrivalDateValid(arrivalDate);
    }

    function isEnrollmentInputsValid() {
        return (
            isReservationNumberValid() &&
            isLastNameValid() &&
            isArrivalDateValidLocal()
        );
    }

    function enrollmentError() {
        if (!errorStatus) {
            return null;
        }
        else if (errorStatus === 404) {
            // Ineligible or reservation not found.
            return (
                <div className="enrollment-card__error">
                    <ErrorAlert type="error">
                        <strong className="error-title">
                            {t('reservationNumberInvalidHeading')}
                        </strong>
                        <div className="error-body">
                            <p>
                                <Trans
                                    i18nKey={'enrollmentRequestInvitation'}
                                    components={[
                                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                                        <a
                                            href={enrollmentRequestUrl}
                                            rel="noreferrer"
                                        />,
                                    ]}
                                />
                            </p>
                        </div>
                    </ErrorAlert>
                </div>
            );
        }
        else {
            // Server errors, client errors, etc.
            return (
                <div className="enrollment-card__error">
                    <ErrorAlert type="error">
                        <strong className="error-title">
                            {t('serviceUnavailableHeading')}
                        </strong>
                        <div className="error-body">
                            <p>
                                {t('serviceUnavailableGuidance')}
                            </p>
                        </div>
                    </ErrorAlert>
                </div>
            );
        }
    }

    return (
        <section className="enrollment-card">
            <header className="enrollment-card__header">
                <div className="enrollment-card__title">
                    {t('enrollYourReservation')}
                </div>
            </header>

            <div className="enrollment-card__body">
                <p className="enrollment-card__hint">
                    <Trans
                        i18nKey={'enrollYourReservationDescription'}
                        components={[
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                                href="https://disneyworld.com/luggage"
                                target="_blank"
                                rel="noreferrer"
                            />,
                        ]}
                    />
                    <br />
                    <strong>{t('enrollYourReservationNote')}</strong>
                </p>

                <label className="field">
                    <span className="field__label">
                        {t('reservationNumber')}
                    </span>
                    <Input
                        className="input"
                        value={reservationId}
                        onChange={(event) =>
                            setReservationId(event.target.value.toUpperCase())
                        }
                        placeholder={t('reservationNumber')}
                        isValid={!reservationId || isReservationNumberValid()}
                        errorMessage={t('reservationNumberInvalid')}
                    />
                </label>

                <label className="field">
                    <span className="field__label">
                        {t('primaryGuestLastName')}
                    </span>
                    <Input
                        className="input"
                        value={reservationLastName}
                        onChange={(event) =>
                            setReservationLastName(event.target.value)
                        }
                        placeholder="Last name"
                        isValid={isLastNameValid()}
                        errorMessage={t('lastNameInvalid')}
                    />
                </label>

                <label className="field">
                    <span className="field__label">{t('arrivalDate')}</span>
                    <Input
                        className="input"
                        type="date"
                        value={arrivalDate}
                        onChange={(event) => setArrivalDate(event.target.value)}
                        isValid={isArrivalDateValidLocal()}
                        errorMessage={arrivalDate && t('arrivalDateInvalid')}
                    />
                </label>

                <div className="enrollment-card__actions">
                    <button
                        type="button"
                        className="btn btn--primary"
                        disabled={
                            !isEnrollmentInputsValid() ||
                            reservationStatusQuery.isFetching
                        }
                        onClick={() => setIsSubmit(true)}
                    >
                        {reservationStatusQuery.isFetching ? (
                            <HorizontalFlex
                                cssClassModifiers={['justify-center']}
                            >
                                <AnimatedLoader />
                            </HorizontalFlex>
                        ) : (
                            t('next')
                        )}
                    </button>
                    {}
                </div>

                <h3>{t('alreadyEnrolled')}</h3>
                <p className="enrollment-card__hint">
                    {t('alreadyEnrolledDescription')}
                </p>
                {enrollmentError()}
            </div>
        </section>
    );
}
